<template>
  <div class="aywa-container">
    <div class="pro-form-card">
      <div class="pro-form-header">
        <div class="header-left">
          <v-icon class="primary-color-assuraf mr-2">flight</v-icon>
          <span class="badge">Assurance Voyage</span>
        </div>
        <div v-if="agentCode" class="header-right">
          <div class="agent-badge" :class="{ 'is-valid': isAgentValid }">
            <i class="fas" :class="isAgentValid ? 'fa-user-check' : 'fa-user-tie'"></i>
            <span v-if="isAgentValid">
              Agent : <strong>{{ agentInfo.prenom }} {{ agentInfo.nom }}</strong> ({{ agentCode }})
            </span>
            <span v-else
              >Code Agent : <strong>{{ agentCode }}</strong></span
            >
          </div>
        </div>
      </div>

      <!-- Stepper iconographique -->
      <div class="stepper">
        <template v-for="(stepInfo, index) in steps">
          <div
            :key="`circle-${stepInfo.id}`"
            class="step-circle"
            :class="{
              'step-circle--inactive': step < stepInfo.id,
              'step-circle--done': step > stepInfo.id
            }"
          >
            <i class="fas" :class="step > stepInfo.id ? 'fa-check' : stepInfo.icon"></i>
            <span class="step-circle__label">{{ stepInfo.label }}</span>
          </div>
          <div
            v-if="index < steps.length - 1"
            :key="`line-${stepInfo.id}`"
            class="step-line"
            :class="{ 'step-line--active': step > stepInfo.id }"
          ></div>
        </template>
      </div>

      <!-- Step Content -->
      <div class="card-body">
        <div v-if="isLoading" class="loading-overlay">
          <div class="loading-spinner">
            <i class="fas fa-spinner fa-spin"></i>
            <p>{{ loadingMessage }}</p>
          </div>
        </div>

        <!-- Étape 1: Saisie des informations client -->
        <div v-if="step === 1">
          <p class="step-subtitle mt-5">Détails du Voyage</p>
          <div class="row">
            <div class="col-sm-4">
              <div class="field-group field-default mb-4 mb-sm-0">
                <label class="field-label">
                  Numéro de téléphone <span class="required-asterisk">*</span>
                </label>
                <div class="phone-input-wrapper">
                  <CountryCodeSelectAywa v-model="countryCode" class="country-select-aywa" />
                  <input
                    id="number_tel"
                    v-model.trim="phoneNumber"
                    type="tel"
                    inputmode="numeric"
                    maxlength="25"
                    class="field-input phone-field-input"
                    :placeholder="phonePlaceholder"
                    name="lead_agent_phone"
                    @input="onPhoneInput"
                  />
                </div>
              </div>
            </div>
            <div class="col-sm-4">
              <div class="field-group field-default mb-4 mb-sm-0">
                <label class="field-label">
                  Pays de destination <span class="required-asterisk">*</span>
                </label>
                <select v-model="destinationCountry" class="field-select" name="destination">
                  <option :value="null">—</option>
                  <option
                    v-for="country in destinationCountries"
                    :key="country._id"
                    :value="country.PAYS"
                  >
                    {{ country.PAYS }}
                  </option>
                </select>
              </div>
            </div>
            <div class="col-sm-4">
              <div class="field-group field-default mb-4 mb-sm-0">
                <label class="field-label">
                  Date de départ <span class="required-asterisk">*</span>
                </label>
                <input
                  type="date"
                  v-model="departureDate"
                  :min="minDepartureDate"
                  class="field-input"
                  name="departure_date"
                />
                <span v-if="departureDate && departureDate < minDepartureDate" class="error-text">
                  La date de départ ne peut pas être passée.
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Étape 2: Upload du passeport et validation des données OCR -->
        <div v-if="step === 2">
          <!-- Section Upload -->
          <div v-if="!ocrSubmitted && !isManualEntry">
            <p class="step-subtitle">Upload passeport</p>
            <div class="fields">
              <div class="row">
                <div class="col-sm-12">
                  <div class="field-group mb-4">
                    <label class="field-label">Passeport</label>
                    <div class="upload-box-wrapper">
                      <div v-if="!passportFile" class="upload-options">
                        <button
                          type="button"
                          class="btn-upload-opt camera"
                          @click="triggerUpload(true)"
                        >
                          <i class="fas fa-camera"></i>
                          <span>Prendre une photo</span>
                        </button>
                        <button
                          type="button"
                          class="btn-upload-opt file"
                          @click="triggerUpload(false)"
                        >
                          <i class="fas fa-file-upload"></i>
                          <span>Choisir un fichier</span>
                        </button>
                      </div>
                      <div v-else class="upload-preview">
                        <div class="file-info">
                          <i class="fas fa-check-circle text-success"></i>
                          <span class="file-name">{{ passportFile.name }}</span>
                        </div>
                        <button type="button" class="btn-remove-file" @click="removeFile">
                          <i class="fas fa-times"></i>
                        </button>
                      </div>
                    </div>

                    <!-- Bascule vers saisie manuelle -->
                    <div class="mode-divider"><span>ou</span></div>
                    <div class="mode-switch-cta">
                      <button type="button" class="btn-link-mode" @click="startManualEntry">
                        Saisie manuelle <i class="fas fa-arrow-right ml-1"></i>
                      </button>
                    </div>

                    <!-- <div class="manual-entry-option mt-4 text-center">
                      <p class="text-muted mb-2">Ou</p>
                      <button type="button" class="btn-manual-entry" @click="startManualEntry">
                        Saisir manuellement les informations <i class="fas fa-edit"></i>
                      </button>
                    </div> -->
                    <input
                      type="file"
                      ref="fileInputPassport"
                      accept="image/*"
                      @change="handleFileUpload"
                      style="display: none"
                    />
                    <input
                      type="file"
                      ref="cameraInputPassport"
                      accept="image/*"
                      capture="environment"
                      @change="handleFileUpload"
                      style="display: none"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div v-if="ocrError" class="error-message" style="font-size: 14px">{{ ocrError }}</div>
          </div>

          <!-- Section Infos Passager (après OCR ou Saisie Manuelle) -->
          <div v-if="ocrSubmitted">
            <p class="step-subtitle">
              {{
                isManualEntry
                  ? 'Veuillez saisir les informations du passager.'
                  : 'Assurez-vous que les données extraites sont correctes.'
              }}
            </p>
            <div v-if="ocrError" class="error-message">
              <p>{{ ocrError }}</p>
              <!-- <button @click="resetOcr">Réessayer</button> -->
            </div>
            <div v-else>
              <!-- OCR Data Details View (Only if not editing) -->
              <div v-if="!isEditingOcr" class="ocr-details-card">
                <div class="details-grid">
                  <div class="detail-item">
                    <span class="detail-label">Nom</span>
                    <span class="detail-value">{{ ocrData.nom || '—' }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Prénom</span>
                    <span class="detail-value">{{ ocrData.prenom || '—' }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">N° Passeport</span>
                    <span class="detail-value">{{ ocrData.numeroPasseport || '—' }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Date de naissance</span>
                    <span
                      class="detail-value"
                      :class="{
                        'text-danger':
                          ocrData.dateNaissance && ocrData.dateNaissance > minDepartureDate
                      }"
                    >
                      {{ ocrData.dateNaissance ? formatDate(ocrData.dateNaissance) : '—' }}
                      <span
                        v-if="ocrData.dateNaissance && ocrData.dateNaissance > minDepartureDate"
                        class="ml-1"
                        >(Invalide)</span
                      >
                    </span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Date d'expiration</span>
                    <span
                      class="detail-value"
                      :class="{
                        'text-danger':
                          ocrData.dateExpiration && ocrData.dateExpiration < minDepartureDate
                      }"
                    >
                      {{ ocrData.dateExpiration ? formatDate(ocrData.dateExpiration) : '—' }}
                      <span
                        v-if="ocrData.dateExpiration && ocrData.dateExpiration < minDepartureDate"
                        class="ml-1"
                        >(Expiré)</span
                      >
                    </span>
                  </div>
                  <div class="detail-item" v-if="ocrData.genre">
                    <span class="detail-label">Genre</span>
                    <span class="detail-value">{{
                      ocrData.genre === 'M' ? 'Masculin' : 'Féminin'
                    }}</span>
                  </div>
                  <div class="detail-item" v-if="ocrData.email">
                    <span class="detail-label">Email</span>
                    <span class="detail-value">{{ ocrData.email }}</span>
                  </div>
                  <div class="detail-item" v-if="ocrData.code_pays && ocrData.nationalite == ''">
                    <span class="detail-label">Nationalité</span>
                    <span class="detail-value">{{ ocrData.code_pays }}</span>
                  </div>
                  <div class="detail-item" v-if="ocrData.nationalite">
                    <span class="detail-label">Nationalité</span>
                    <span class="detail-value">{{ ocrData.nationalite }}</span>
                  </div>
                </div>
              </div>

              <!-- Editable Form -->
              <div v-if="isEditingOcr" class="fields">
                <div class="row">
                  <div class="col-sm-4">
                    <div class="field-group field-default mb-4">
                      <label class="field-label"
                        >Nom <span class="required-asterisk">*</span></label
                      >
                      <input v-model="ocrData.nom" class="field-input" />
                    </div>
                  </div>
                  <div class="col-sm-4">
                    <div class="field-group field-default mb-4">
                      <label class="field-label"
                        >Prénom <span class="required-asterisk">*</span></label
                      >
                      <input v-model="ocrData.prenom" class="field-input" />
                    </div>
                  </div>
                  <div class="col-sm-4">
                    <div class="field-group field-default mb-4">
                      <label class="field-label"
                        >N° Passeport <span class="required-asterisk">*</span></label
                      >
                      <input v-model="ocrData.numeroPasseport" class="field-input" />
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-sm-6">
                    <div class="field-group field-default mb-4">
                      <label class="field-label"
                        >Date de naissance <span class="required-asterisk">*</span></label
                      >
                      <input type="date" v-model="ocrData.dateNaissance" class="field-input" />
                      <span
                        v-if="ocrData.dateNaissance && ocrData.dateNaissance > minDepartureDate"
                        class="error-text"
                      >
                        La date de naissance ne peut pas être future.
                      </span>
                    </div>
                  </div>
                  <div class="col-sm-6">
                    <div class="field-group field-default mb-4">
                      <label class="field-label"
                        >Date d'expiration <span class="required-asterisk">*</span></label
                      >
                      <input type="date" v-model="ocrData.dateExpiration" class="field-input" />
                      <span
                        v-if="ocrData.dateExpiration && ocrData.dateExpiration < minDepartureDate"
                        class="error-text"
                      >
                        Le passeport est expiré.
                      </span>
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-sm-6">
                    <div class="field-group field-default mb-4">
                      <label class="field-label"
                        >Genre <span class="required-asterisk">*</span></label
                      >
                      <select v-model="ocrData.genre" class="field-select">
                        <option value="">—</option>
                        <option value="M">Masculin</option>
                        <option value="F">Féminin</option>
                      </select>
                    </div>
                  </div>
                  <div class="col-sm-6">
                    <div class="field-group field-default mb-4">
                      <label class="field-label"
                        >Nationalité <span class="required-asterisk">*</span></label
                      >
                      <select v-model="ocrData.nationalite" class="field-select">
                        <option value="">—</option>
                        <option
                          v-for="country in listCountry"
                          :key="country._id"
                          :value="country.NATIONALITE || country.PAYS"
                        >
                          {{ country.NATIONALITE || country.PAYS }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Étape 3: Validation (Choix durée + Devis + Paiement) -->
        <div v-if="step === 3">
          <!-- Section Choix de la durée (avant création du devis) -->
          <div>
            <p class="step-subtitle">Sélectionnez la période de votre contrat.</p>
            <div class="duration-options">
              <button
                v-for="option in durationOptions"
                :key="option.value"
                @click="selectDuration(option.value)"
                :class="['duration-btn', { selected: selectedDuration === option.value }]"
              >
                <div class="duration-content">
                  <span class="duration-label">{{ option.label }}</span>
                  <!-- <span class="duration-price">{{ option.price }} FCFA</span> -->
                </div>
              </button>
            </div>
          </div>
        </div>

        <!-- Étape 4: Confirmation (Paiement + Résultat) - FUSIONNÉE AVEC STEP 3 -->
        <div v-if="step === 4">
          <!-- Section Résumé Devis + Paiement (après création du devis) -->
          <div v-if="devis && devis.numeroDevis && !contratDetails" class="checkout-card">
            <div class="checkout-header">
              <i class="fas fa-plane-departure checkout-icon"></i>
              <h3 class="checkout-title">Récapitulatif de votre voyage</h3>
            </div>
            <div class="checkout-rows">
              <div class="checkout-row">
                <span class="checkout-label">Numéro de devis</span>
                <span class="checkout-value">{{ devis.numeroDevis }}</span>
              </div>
              <div class="checkout-row">
                <span class="checkout-label">Voyageur</span>
                <span class="checkout-value">{{ ocrData.prenom }} {{ ocrData.nom }}</span>
              </div>
              <div class="checkout-row">
                <span class="checkout-label">Destination</span>
                <span class="checkout-value">{{ destinationCountry }}</span>
              </div>
              <div class="checkout-row">
                <span class="checkout-label">Date de départ</span>
                <span class="checkout-value">{{ formatDate(departureDate) }}</span>
              </div>
              <div class="checkout-row">
                <span class="checkout-label">Date de retour</span>
                <span class="checkout-value">{{ formatDate(returnDate) }}</span>
              </div>
              <div class="checkout-row">
                <span class="checkout-label">Durée</span>
                <span class="checkout-value">{{ selectedDurationLabel }}</span>
              </div>
            </div>

            <!-- Breakdown des frais -->
            <div class="checkout-breakdown">
              <div class="checkout-breakdown-row" v-if="devis.prime_nette != null">
                <span class="checkout-breakdown-label">Prime nette</span>
                <span class="checkout-breakdown-value"
                  >{{ Number(devis.prime_nette).toLocaleString('fr-FR') }} FCFA</span
                >
              </div>
              <div class="checkout-breakdown-row" v-if="devis.taxe != null">
                <span class="checkout-breakdown-label">Taxe</span>
                <span class="checkout-breakdown-value"
                  >{{ Number(devis.taxe).toLocaleString('fr-FR') }} FCFA</span
                >
              </div>
              <div
                class="checkout-breakdown-row"
                v-if="devis.accessoires != null && Number(devis.accessoires) > 0"
              >
                <span class="checkout-breakdown-label">Accessoires</span>
                <span class="checkout-breakdown-value"
                  >{{ Number(devis.accessoires).toLocaleString('fr-FR') }} FCFA</span
                >
              </div>
            </div>

            <div class="checkout-total">
              <span class="checkout-total-label">Total à payer</span>
              <span class="checkout-total-amount">
                {{ Number(devis.prime_ttc).toLocaleString('fr-FR') }}
                <span class="checkout-total-currency">FCFA</span>
              </span>
            </div>
          </div>

          <!-- Section Paiement (sticky sur mobile) -->
          <div class="payment-section mt-4" v-if="!contratDetails">
            <p class="payment-section-title">Choisissez votre moyen de paiement</p>
            <div class="payment-options">
              <div class="payment-buttons">
                <button @click="payWithWave" class="payment-btn wave">
                  <img src="../../../assets/img/logoPaiement/wave.png" alt="Wave" />
                  <span class="payment-btn-label">Wave</span>
                </button>
                <button @click="payWithOrangeMoney" class="payment-btn om">
                  <img src="../../../assets/img/logoPaiement/orange-money.png" alt="Orange Money" />
                  <span class="payment-btn-label">Orange Money</span>
                </button>
              </div>
            </div>
            <p class="payment-security-note">
              <i class="fas fa-shield-alt"></i> Paiement 100 % sécurisé
            </p>
          </div>

          <!-- Affiche la confirmation après paiement -->
          <div v-if="contratDetails" class="confirmation-summary">
            <p>Votre assurance voyage Tukki a été validée avec succès.</p>
            <ul>
              <li>
                <strong>Numéro de police :</strong> {{ contratDetails.contrat.numero_police }}
              </li>
              <li>
                <strong>Date d'effet :</strong> {{ formatDate(contratDetails.contrat.dateEffet) }}
              </li>
              <li>
                <strong>Date d'échéance :</strong>
                {{ formatDate(contratDetails.contrat.dateEcheance) }}
              </li>
              <li><strong>Montant payé :</strong> {{ contratDetails.contrat.prime_ttc }} FCFA</li>
            </ul>
            <button
              v-if="!contratDetails.attestation"
              class="btn-refresh"
              @click="refreshContratDetails"
              :disabled="isLoading"
            >
              <i :class="['fas', 'fa-sync-alt', { 'fa-spin': isLoading }]"></i>
              Réactualiser pour obtenir le talon
            </button>
          </div>

          <div v-if="contratDetails && contratDetails.attestation" class="sharing-options">
            <div class="sharing-header">
              <h3>Partager votre attestation de voyage</h3>
            </div>
            <div class="share-buttons-container">
              <button class="share-btn pdf" @click="share('pdf')">
                <i class="fas fa-file-pdf"></i> Télécharger l'attestation
              </button>
              <button class="share-btn whatsapp" @click="share('whatsapp')">
                <i class="fab fa-whatsapp"></i> Partager sur WhatsApp
              </button>
              <button class="share-btn sms" @click="share('sms')">
                <i class="fas fa-sms"></i> Envoyer par SMS
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Card Footer -->
      <div class="card-footer">
        <!-- Bouton pour passer à l'étape suivante -->
        <div class="actions">
          <div class="left-actions">
            <button
              v-if="step > 1 && step !== 4 && step !== 7"
              @click="prevStep"
              type="button"
              class="wizard-btn back-btn"
            >
              <v-icon>&#xe5c4;</v-icon>
              <span class="ml-2">{{
                $t('assur_lead.form_assur_lead.second_step.back_button_label')
              }}</span>
            </button>
          </div>

          <div class="right-actions">
            <!-- Bouton Annuler pour mode édition étape 2 -->
            <button
              v-if="step === 2 && ocrSubmitted && isEditingOcr"
              @click="cancelEditOcr"
              class="wizard-btn cancel-btn mr-4"
            >
              <span>Annuler</span>
            </button>

            <!-- Bouton Suivant normal -->
            <button
              v-if="step === 1"
              @click="nextStep"
              :disabled="!isStepValid"
              class="btn-primary-modern"
            >
              <span>Suivant</span>
            </button>

            <button
              v-if="step === 2 && !ocrSubmitted"
              @click="submitOcr"
              :disabled="!passportFile || isLoading"
              class="btn-primary-modern"
            >
              <span>Analyser le passeport</span>
            </button>

            <!-- Bouton Re-uploader pour le step 2 (après OCR) -->
            <button
              v-if="step === 2 && ocrSubmitted && !isEditingOcr"
              @click="resetOcr"
              class="btn-outline-primary"
            >
              <i class="fas fa-upload"></i>
              <span class="ml-2">Re-uploader</span>
            </button>

            <!-- Bouton Modifier pour le step 2 (après OCR) -->
            <button
              v-if="step === 2 && ocrSubmitted && !isEditingOcr && !ocrError"
              @click="toggleEditOcr"
              class="btn-outline-primary"
            >
              <i class="fas fa-pen"></i>
              <span class="ml-2">Modifier</span>
            </button>

            <!-- Bouton Valider pour mode édition étape 2 -->
            <button
              v-if="step === 2 && ocrSubmitted && isEditingOcr"
              @click="confirmEditOcr"
              :disabled="!isOcrDataValid"
              class="btn-primary-modern"
            >
              <span>Valider</span>
            </button>

            <!-- Bouton Suivant pour step 2 (après OCR) -->
            <button
              v-if="step === 2 && ocrSubmitted && !isEditingOcr && !ocrError"
              @click="nextStep"
              class="btn-primary-modern"
            >
              <span>Suivant</span>
            </button>

            <button
              v-if="step === 3"
              @click="nextStep"
              :disabled="!selectedDuration || isLoading"
              class="btn-primary-modern"
            >
              <span>Obtenir le devis</span>
            </button>

            <button
              v-if="step === 4 && !contratDetails"
              @click="restartSubscription"
              type="button"
              class="wizard-btn cancel-btn"
            >
              <i class="fas fa-redo mr-2"></i>
              <span>Recommencer</span>
            </button>
          </div>
        </div>
        <button @click="resubscribe" v-if="step === 3 && contratDetails" class="btn-primary-modern">
          Resouscrire
        </button>
      </div>
    </div>

    <!-- Modale de paiement Orange Money -->
    <div v-if="showOrangeMoneyModal" class="modal-overlay">
      <div class="modal-content">
        <h3>Paiement par Orange Money</h3>
        <div class="form-group">
          <label for="om-phone">Numéro de téléphone Orange</label>
          <input type="tel" id="om-phone" v-model="omPhoneNumber" placeholder="77xxxxxxx" />
        </div>
        <div class="form-group">
          <label for="om-otp">Code OTP (#144#391#)</label>
          <input type="text" id="om-otp" v-model="omOtp" placeholder="Code à 6 chiffres" />
        </div>
        <div class="modal-actions">
          <button @click="submitOrangeMoneyPayment" :disabled="!isOmFormValid || isLoading">
            Valider
          </button>
          <button @click="showOrangeMoneyModal = false" class="btn-secondary">Annuler</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
::placeholder {
  /* Chrome, Firefox, Opera, Safari 10.1+ */
  font-size: 12px;
}

/* Mode switch (OCR <-> Manual) */
.mode-divider {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 18px 0 10px;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
}
.mode-divider::before,
.mode-divider::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid rgba(13, 78, 120, 0.12);
}
.mode-divider span {
  padding: 0 14px;
}

.mode-switch-cta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 0px 0 0px;
}

.btn-link-mode {
  background: none;
  border: none;
  color: #ffa409;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 6px;
  transition: background 0.2s ease;
}
.btn-link-mode:hover {
  background: rgba(255, 164, 9, 0.08);
}

.btn-link-mode-back {
  background: none;
  border: none;
  color: #0d4e78;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 0;
  margin-bottom: 6px;
}
.btn-link-mode-back:hover {
  color: #ffa409;
}
.pro-form-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 10px 24px rgba(13, 78, 120, 0.15);
  padding: 20px;
  max-width: 880px;
  width: 100%;
  margin: 0 auto;
  position: relative;
}

.badge {
  font-size: 12px;
  color: #0d4e78;
  background: rgba(13, 78, 120, 0.08);
  border: 1px solid rgba(13, 78, 120, 0.14);
  padding: 6px 10px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
}

@media (max-width: 768px) {
  .badge {
    font-size: 11px;
    padding: 6px 8px;
  }
}

.pro-form-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(13, 78, 120, 0.12);
  margin-bottom: 16px;
  gap: 15px;
}

.header-left,
.header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-right {
  flex-shrink: 0;
}

@media (max-width: 600px) {
  .pro-form-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  .header-right {
    width: 100%;
  }
  .agent-badge {
    width: 100%;
    justify-content: center;
  }
}

.titles .title {
  margin: 0;
  font-size: 18px;
  color: #0d4e78;
  font-weight: 700;
  line-height: 1.2;
}

.titles .subtitle {
  margin: 2px 0 0;
  font-size: 13px;
  color: #515457;
}

.fields {
  margin-bottom: 10px;
}

.row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.col-sm-12 {
  flex: 1 1 100%;
}
.col-sm-6 {
  flex: 1 1 calc(50% - 12px);
  min-width: 260px;
}
.col-sm-3 {
  flex: 1 1 calc(25% - 12px);
  min-width: 200px;
}
.col-sm-4 {
  flex: 1 1 calc(30% - 12px);
  min-width: 200px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label {
  font-weight: 600;
  color: #0d4e78;
  font-size: 13px;
}

.field-input,
.field-select,
textarea.field-input {
  width: 100%;
  height: 45px;
  border: 1px solid rgba(13, 78, 120, 0.22);
  border-radius: 10px;
  padding: 10px 12px;
  outline: none;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
  background: #fff;
  color: #1c2b36;
  font-size: 14px;
}

.field-input:focus,
.field-select:focus,
textarea.field-input:focus {
  border-color: #ffa409;
  box-shadow: 0 0 0 3px rgba(255, 164, 9, 0.18);
}

/* New Upload Styles */
.upload-box-wrapper {
  background: #f8fafc;
  border: 2px dashed rgba(13, 78, 120, 0.2);
  border-radius: 12px;
  padding: 15px;
  transition: all 0.3s ease;
}

.upload-box-wrapper:hover {
  border-color: #ffa409;
  background: #fff;
}

.upload-options {
  display: flex;
  gap: 10px;
}

.btn-upload-opt {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  color: #0d4e78;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-upload-opt i {
  font-size: 18px;
  color: #ffa409;
}

.btn-upload-opt.camera {
  display: none; /* Masqué par défaut sur desktop */
}

.btn-upload-opt:hover {
  border-color: #ffa409;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.upload-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #ecfdf5;
  border-radius: 8px;
  border: 1px solid #10b981;
}

.file {
  width: 500px;
  margin-bottom: 0px;
  margin-left: 0%;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
}

.file-name {
  font-size: 13px;
  color: #065f46;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
}

.btn-remove-file {
  background: none;
  border: none;
  color: #ef4444;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background 0.2s;
}

.btn-remove-file:hover {
  background: rgba(239, 68, 68, 0.1);
}

@media (max-width: 600px) {
  .upload-options {
    flex-direction: column;
    gap: 12px;
  }

  .btn-upload-opt {
    padding: 15px;
    font-size: 14px;
    flex-direction: row;
    justify-content: center;
    width: 100%;
    border: 2px solid #e2e8f0;
  }

  .btn-upload-opt.camera {
    display: flex; /* Affiché uniquement sur mobile */
    background: #fff8ed;
    border-color: #ffa409;
  }

  .btn-upload-opt i {
    font-size: 20px;
  }
}

.success-alert {
  border-color: rgba(13, 78, 120, 0.22) !important;
}

.actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  flex-wrap: wrap;
  gap: 10px;
}

.left-actions,
.right-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

@media (max-width: 600px) {
  .card-footer {
    padding: 1rem;
  }
  .actions {
    flex-direction: column-reverse;
    gap: 15px;
  }
  .left-actions,
  .right-actions {
    width: 100%;
    justify-content: center;
  }
  .btn-primary-modern,
  .btn-outline-primary,
  .wizard-btn {
    width: 100%;
    padding: 10px 15px;
    font-size: 14px;
    justify-content: center;
  }
}

.back-btn,
.cancel-btn {
  color: #333;
  background: none;
  border: none;
  font-weight: 500;
  cursor: pointer;
}

.cancel-btn {
  font-size: 1.5rem;
}

.required-asterisk {
  color: red;
}

.agent-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background-color: #f0f2f5;
  border-radius: 999px;
  color: #495057;
  font-size: 13px;
  border: 1px solid #e2e8f0;
  white-space: nowrap;
}

.agent-badge.is-valid {
  background-color: #ecfdf5;
  color: #065f46;
  border-color: #10b981;
}

.agent-badge i {
  color: #ffa409;
  font-size: 14px;
}

.agent-badge.is-valid i {
  color: #10b981;
}

.agent-badge strong {
  font-weight: 600;
}

.btn-manual-entry {
  background: none;
  border: 1px solid #0d4e78;
  color: #0d4e78;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-manual-entry:hover {
  background-color: rgba(13, 78, 120, 0.05);
}

.text-muted {
  color: #6c757d;
  font-size: 13px;
}

/* ============== Ancien css*/
.devis-details p {
  font-size: 1.3rem;
  color: #333;
}
.devis-details .price {
  font-size: 2rem;
}

.aywa-container {
  max-width: 1000px;
  margin: 2rem auto;
  font-family: 'Poppins', sans-serif;
  color: #333;
}

.card {
  background-color: #fff;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #f0f0f0;
}

.back-btn {
  margin-top: -2px !important;
  background: none;
  /*border: 1px solid #e0e0e0;*/

  cursor: pointer;
  color: #555;
  transition: all 0.3s ease;
}

/*.back-btn:hover {
  background-color: #f5f5f5;
  border-color: #ccc;
}*/

.card-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

/* Stepper iconographique (mockup parcours-identite) */
.stepper {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 1.5rem 0.5rem 2.4rem;
}
.step-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #ffa409;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  position: relative;
  box-shadow: 0 2px 8px rgba(255, 164, 9, 0.35);
  flex-shrink: 0;
  transition:
    background 0.3s ease,
    box-shadow 0.3s ease;
}
.step-circle--inactive {
  background: #f3f4f6;
  color: #9ca3af;
  box-shadow: none;
}
.step-circle__label {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 6px;
  font-size: 10px;
  color: #6b7280;
  font-weight: 600;
  text-align: center;
  line-height: 1.2;
  white-space: nowrap;
}
.step-line {
  flex: 1;
  height: 2px;
  background: #e5e7eb;
  transition: background 0.3s ease;
}
.step-line--active {
  background: #ffa409;
}

@media (max-width: 600px) {
  .stepper {
    gap: 6px;
    margin: 1rem 0.25rem 1.8rem;
  }
  .step-circle {
    width: 26px;
    height: 26px;
    font-size: 11px;
  }
  .step-circle__label {
    font-size: 8px;
    margin-top: 4px;
  }
}
.card-body {
  padding: 0rem 2.5rem;
}

.step-header {
  text-align: center;
  margin-bottom: 2rem;
}

.step-title-main {
  font-size: 1.8rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
}

.step-description {
  font-size: 1rem;
  color: #777;
}

.form-row {
  display: flex;
  gap: 1.5rem;
}

.form-row .form-group {
  flex: 1;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #555;
}

.form-control-modern {
  padding: 0.8rem 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
}

.form-control-modern:focus {
  outline: none;
  border-color: #ffa409;
  box-shadow: 0 0 0 2px rgba(0, 165, 165, 0.1);
}

.card-footer {
  display: flex;
  justify-content: center;
  padding: 1.5rem;
  /*//background-color: #f9f9f9;*/
  border-top: 1px solid #fff;
  gap: 1rem;
}

.btn-primary-modern,
.btn-secondary-modern {
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
}

.btn-primary-modern {
  background-color: #ffa409;
  color: #fff;
}

.btn-primary-modern:hover {
  background-color: #008a8a;
}

.btn-primary-modern:disabled {
  background-color: #d3d3d3;
  cursor: not-allowed;
}

.btn-secondary-modern {
  background-color: #e0e0e0;
  color: #333;
}

.btn-secondary-modern:hover {
  background-color: #d1d1d1;
}

/* Upload Section */
.upload-section {
  display: flex;
  gap: 1rem;
  justify-content: center;
}
.upload-box {
  border: 2px dashed #e0e0e0;
  border-radius: 10px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
}
.upload-box:hover {
  border-color: #ffa409;
  background-color: #f7fdfd;
}
.upload-label {
  color: #555;
  font-weight: 500;
}
.file-input {
  display: none;
}

/* Duration Options consolidated at the end of file */

/* Payment Options */
.payment-options {
  text-align: center;
}
.payment-buttons {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin-top: 1rem;
}
.payment-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1.5rem;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
  background-color: #fff;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 150px;
  color: #0a0a0a;
}
.payment-btn:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
}
.payment-btn img {
  height: 40px;
}

/* Devis Summary */
.devis-summary,
.confirmation-summary {
  background-color: #f9f9f9;
  border-radius: 10px;
  padding: 1.5rem;
}
.insurer-logo {
  max-height: 40px;
  margin-bottom: 1rem;
}
.price {
  font-size: 1.2rem;
  font-weight: 600;
  margin-top: 1rem;
  color: #ffa409;
}

/* Loading Overlay */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
}
.loading-spinner {
  text-align: center;
  font-size: 1.2rem;
}
.loading-spinner i {
  font-size: 3rem;
  color: #ffa409;
  margin-bottom: 1rem;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}
.modal-content {
  background: #fff;
  padding: 2rem;
  border-radius: 15px;
  width: 90%;
  max-width: 450px;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
}
</style>

<script>
import PassportOcrService from '@/services/PassportOcrService';
import TukkiService from './Services/TukkiService';
import { countryController } from '@/packages/country/country.controller';
import Api from '@/services/Api';
import CountryCodeSelectAywa from '../../../views/CountryCodeSelectAywa.vue';
import phoneDigitsMap from '../../../common/phoneDigits.json';
import { sanitizePhoneNumber } from '@/common/sanitizePhoneNumber';

export default {
  name: 'TukkiSubscription',
  components: {
    CountryCodeSelectAywa
  },
  props: {
    id_association: {
      type: String,
      required: false
    },
    agentCodeFromUrl: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      step: 1,
      // Step 1 data
      nom: '',
      prenom: '',
      phoneNumber: '',
      countryCode: '+221',
      agentCode: null,
      destinationCountry: null,
      departureDate: null,
      listCountry: [],
      // Step 2 data
      passportFile: null,
      ocrSubmitted: false,
      // Step 3 data
      ocrData: {
        nom: '',
        prenom: '',
        numeroPasseport: '',
        dateNaissance: '',
        dateExpiration: '',
        email: '',
        nationalite: '',
        paysResidence: '',
        genre: ''
      },
      ocrError: null,
      // Step 4 data
      durationOptions: [
        { value: 7, label: '7 jours', price: 5000 },
        { value: 15, label: '15 jours', price: 8000 },
        { value: 30, label: '30 jours', price: 12000 },
        { value: 90, label: '90 jours', price: 25000 },
        { value: 180, label: '180 jours', price: 45000 },
        { value: 365, label: '365 jours', price: 80000 }
      ],
      selectedDuration: null,
      // Global state
      isLoading: false,
      isEditingOcr: false,
      isManualEntry: false,
      loadingMessage: '',
      devis: null,
      contratDetails: null,
      pollingIntervalId: null,
      pollingTimeoutId: null,
      agentInfo: null,
      isAgentValid: false,
      // Orange Money Modal
      showOrangeMoneyModal: false,
      omPhoneNumber: '',
      omOtp: '',
      // Stepper data
      steps: [
        { id: 1, label: 'Voyage', icon: 'fa-plane-departure' },
        { id: 2, label: 'Passeport', icon: 'fa-passport' },
        { id: 3, label: 'Durée', icon: 'fa-calendar-alt' },
        { id: 4, label: 'Paiement', icon: 'fa-credit-card' }
      ],
      ToastTabSucess: {
        theme: 'toasted-primary',
        position: 'top-center',
        duration: 4000,
        fitToScreen: true,
        type: 'success',
        className: 'custom-success-class'
      },
      ToastTabError: {
        theme: 'toasted-primary',
        position: 'top-center',
        duration: 4000,
        fitToScreen: true,
        type: 'error'
      }
    };
  },
  computed: {
    // Le Sénégal est exclu des destinations mais doit rester disponible comme nationalité
    destinationCountries() {
      return this.listCountry.filter((country) => country.PAYS !== 'Sénégal');
    },
    countryPhoneRule() {
      var cc = this.countryCode;
      var digits = phoneDigitsMap[cc];
      if (!digits) {
        var prefix =
          '+' +
          String(cc)
            .replace(/[^0-9]/g, '')
            .slice(0, 2);
        digits = phoneDigitsMap[prefix] || 9;
      }
      return { digits: digits };
    },
    phonePlaceholder() {
      var rule = this.countryPhoneRule;
      var d = rule.digits;
      var groups = [];
      for (var i = 0; i < d; i += 2) {
        var n = Math.min(2, d - i);
        groups.push(n === 2 ? 'XX' : 'X');
      }
      return groups.join(' ') + ' (' + d + ' chiffres)';
    },
    resolvedAssociationId() {
      const fromRoute =
        this.$route && this.$route.params ? this.$route.params.id_association : undefined;
      return this.id_association || fromRoute || null;
    },
    minDepartureDate() {
      return new Date().toISOString().split('T')[0];
    },
    isStepValid() {
      switch (this.step) {
        case 1:
          var phone = String(this.phoneNumber || '').replace(/[^0-9]/g, '');
          var rule = this.countryPhoneRule;
          if (
            phone.length > rule.digits &&
            phone.length === rule.digits + 1 &&
            phone.charAt(0) === '0'
          ) {
            phone = phone.slice(1);
          }
          return (
            phone.length === rule.digits &&
            this.destinationCountry &&
            this.departureDate &&
            this.departureDate >= this.minDepartureDate
          );
        case 2:
          return (
            !this.ocrError &&
            this.ocrSubmitted &&
            this.ocrData.nom &&
            this.ocrData.prenom &&
            this.ocrData.numeroPasseport &&
            this.ocrData.dateExpiration &&
            this.ocrData.dateExpiration >= this.minDepartureDate &&
            (!this.ocrData.dateNaissance || this.ocrData.dateNaissance <= this.minDepartureDate) &&
            this.ocrData.genre &&
            this.ocrData.nationalite
          );
        case 3:
          return this.selectedDuration !== null;
        case 4:
          return this.devis && this.contratDetails;
        default:
          return true;
      }
    },
    isOmFormValid() {
      return this.omPhoneNumber.length === 9 && this.omOtp.length === 6;
    },
    isOcrDataValid() {
      return (
        !this.ocrError &&
        this.ocrData.nom &&
        this.ocrData.prenom &&
        this.ocrData.numeroPasseport &&
        this.ocrData.dateExpiration &&
        this.ocrData.dateExpiration >= this.minDepartureDate &&
        (!this.ocrData.dateNaissance || this.ocrData.dateNaissance <= this.minDepartureDate) &&
        this.ocrData.genre &&
        this.ocrData.nationalite
      );
    },
    returnDate() {
      if (!this.departureDate || !this.selectedDuration) return null;
      const date = new Date(this.departureDate);
      date.setDate(date.getDate() + this.selectedDuration);
      return date.toISOString().split('T')[0];
    },
    selectedDurationLabel() {
      const opt = this.durationOptions.find((o) => o.value === this.selectedDuration);
      return opt ? opt.label : '';
    }
  },
  methods: {
    onPhoneInput() {
      this.phoneNumber = sanitizePhoneNumber(this.phoneNumber, this.countryCode);
    },
    saveUserPassport(passportData, user) {
      let genre;
      if (passportData.genre === 'M') {
        genre = 'homme';
      } else {
        genre = 'femm';
      }
      const UserPassport = {
        userid: user,
        prenom_passager: passportData.prenom,
        nom_passager: passportData.nom,
        nationalite_passager: passportData.nationalite,
        //nationalite_passager: "Sénégal",
        numero_ci_passager: passportData.numeroPasseport,
        dateNaissance: passportData.dateNaissance,
        date_delivrance: null,
        date_expiration: passportData.dateExpiration,
        genre: genre
      };

      Api()
        .post('mypassports/save', UserPassport)
        .then((response) => {
          console.log(response);
          //this.$toasted.show("Passport enregistré avec succés !", this.ToastTabSucess)
        });
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('fr-FR', options);
    },
    normalizeGenre(value) {
      if (!value) return '';
      const normalized = value.toString().trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
      if (['m', 'male', 'masculin', 'homme', 'h'].includes(normalized)) return 'M';
      if (['f', 'female', 'feminin', 'femme'].includes(normalized)) return 'F';
      return '';
    },
    normalizeAgentCode(value) {
      return String(value || '')
        .trim()
        .toUpperCase()
        .replace(/\s+/g, '');
    },

    prevStep() {
      if (this.step > 1) {
        this.step--;
      }
    },
    nextStep() {
      if (this.step === 3) {
        this.createDevis();
      } else {
        this.step++;
      }
    },
    triggerUpload(isCamera) {
      const refName = isCamera ? 'cameraInputPassport' : 'fileInputPassport';
      const input = this.$refs[refName];
      if (input) {
        input.click();
      }
    },
    removeFile() {
      this.passportFile = null;
    },
    async handleFileUpload(event) {
      const rawFile = event.target.files[0];
      if (!rawFile) return;

      try {
        const processed = await PassportOcrService.preprocessImage(rawFile);
        const validation = PassportOcrService.validateFile(processed);
        if (!validation.valid) {
          this.$toasted.show('Fichier invalide', this.ToastTabError);
          return;
        }
        this.passportFile = processed;
      } catch (err) {
        console.error('Erreur preprocessing passeport:', err);
        this.$toasted.show(
          "Impossible de lire l'image. Réessayez avec une photo JPEG ou PNG.",
          this.ToastTabError
        );
      }
    },
    async submitOcr() {
      this.loadingMessage = 'Analyse du passeport en cours...';
      this.isLoading = true;
      this.ocrError = null;

      try {
        if (!this.passportFile) {
          this.ocrError = 'Veuillez sélectionner votre passeport.';
          this.isLoading = false;
          return;
        }
        const data = await PassportOcrService.scanPassport(this.passportFile);

        this.ocrData = {
          ...this.ocrData,
          ...data,
          genre: this.normalizeGenre(data.sexe)
        };

        if (this.ocrData.dateExpiration && this.ocrData.dateExpiration < this.minDepartureDate) {
          this.ocrError = 'Attention : Le passeport détecté est expiré.';
        }

        this.isEditingOcr = false;
        this.ocrSubmitted = true;
      } catch (error) {
        console.error('Erreur OCR:', error);
        this.ocrError = "Une erreur est survenue lors de l'analyse du passeport.";
      } finally {
        this.isLoading = false;
      }
    },
    selectDuration(duration) {
      this.selectedDuration = duration;
    },
    async createDevis() {
      this.loadingMessage = 'Création du devis en cours...';
      this.isLoading = true;
      try {
        const normalizedAgentCode = this.normalizeAgentCode(this.agentCode);
        this.agentCode = normalizedAgentCode;

        const devisData = {
          telephone: this.countryCode + sanitizePhoneNumber(this.phoneNumber, this.countryCode),
          pays: this.destinationCountry,
          dateDepart: this.departureDate,
          duree: this.selectedDuration,
          codeAgent: normalizedAgentCode,
          passagers: [
            {
              nom: this.ocrData.nom,
              prenom: this.ocrData.prenom,
              dateNaissance: this.ocrData.dateNaissance,
              numeroPasseport: this.ocrData.numeroPasseport,
              dateExpiration: this.ocrData.dateExpiration,
              email: this.ocrData.email,
              nationalite: this.ocrData.nationalite || this.ocrData.code_pays,
              paysResidence: this.ocrData.paysResidence,
              genre: this.ocrData.genre
            }
          ]
        };

        const result = await TukkiService.createDevis(devisData);
        this.devis = result;
        let passportData = devisData.passagers[0];
        let user = this.devis.userid;
        // Sauvegarder le passport
        this.saveUserPassport(passportData, user);
        this.step = 4;
      } catch (error) {
        this.$toasted.show('La création du devis a échoué', this.ToastTabError);
      } finally {
        this.isLoading = false;
      }
    },
    async payWithWave() {
      this.loadingMessage = 'Initialisation du paiement Wave...';
      this.isLoading = true;
      try {
        const paymentData = {
          numeroDevis: this.devis.numeroDevis,
          montant: this.devis.prime_ttc
        };
        const result = await TukkiService.initiateWavePayment(paymentData);
        if (result.wave_launch_url) {
          window.location.href = result.wave_launch_url;
        } else {
          throw new Error("L'URL de paiement Wave n'a pas été reçue.");
        }
      } catch (error) {
        console.error('Erreur paiement Wave:', error);
        this.$toasted.show(
          "L'initialisation du paiement Wave a échoué. Veuillez réessayer.",
          this.ToastTabError
        );
        this.isLoading = false;
      }
    },
    payWithOrangeMoney() {
      this.omPhoneNumber = '';
      this.omOtp = '';
      this.showOrangeMoneyModal = true;
    },
    async submitOrangeMoneyPayment() {
      this.loadingMessage = 'Initialisation du paiement Orange Money...';
      this.isLoading = true;
      this.showOrangeMoneyModal = false;
      try {
        const paymentData = {
          numeroDevis: this.devis.numeroDevis,
          montant: this.devis.prime_ttc,
          telephone: this.omPhoneNumber,
          otp: this.omOtp
        };
        await TukkiService.payWithOrangeMoney(paymentData);
        this.startPollingForContrat(paymentData.numeroDevis);
      } catch (error) {
        console.error('Erreur paiement OM:', error);
        const apiMessage =
          error &&
          error.response &&
          error.response.data &&
          (error.response.data.message || error.response.data.error);
        this.$toasted.show(
          apiMessage ||
            'Le paiement Orange Money a échoué. Vérifiez votre numéro et votre code OTP.',
          this.ToastTabError
        );
        this.showOrangeMoneyModal = true;
        this.isLoading = false;
      }
    },
    async refreshContratDetails() {
      if (!this.devis || !this.devis.numeroDevis) return;
      this.loadingMessage = 'Mise à jour des détails...';
      this.isLoading = true;
      try {
        const details = await TukkiService.getContratDetails({
          numeroDevis: this.devis.numeroDevis
        });
        if (details && details.contrat) {
          this.contratDetails = details;
          this.$toasted.show('Détails mis à jour', this.ToastTabSucess);
        }
      } catch (error) {
        this.$toasted.show('Erreur de mise à jour', this.ToastTabError);
      } finally {
        this.isLoading = false;
      }
    },
    share(method) {
      if (!this.contratDetails || !this.contratDetails.attestation) return;
      const attestationLink = this.contratDetails.attestation.linkAttestation;
      const summary = `Votre attestation Tukki : ${attestationLink}`;
      switch (method) {
        case 'pdf':
          window.open(attestationLink, '_blank');
          break;
        case 'whatsapp':
          window.open(`https://wa.me/?text=${encodeURIComponent(summary)}`, '_blank');
          break;
        case 'sms':
          window.open(`sms:?body=${encodeURIComponent(summary)}`, '_blank');
          break;
      }
    },
    startPollingForContrat(numeroDevis) {
      this.loadingMessage = 'Confirmation du paiement...';
      this.isLoading = true;
      const poll = async () => {
        try {
          const details = await TukkiService.getContratDetails({ numeroDevis });
          if (details && details.contrat) {
            this.contratDetails = details;
            this.stopPolling();
          }
        } catch (error) {}
      };
      this.pollingIntervalId = setInterval(poll, 5000);
      this.pollingTimeoutId = setTimeout(() => this.stopPolling(true), 300000);
    },
    stopPolling(isTimeout = false) {
      if (this.pollingIntervalId) clearInterval(this.pollingIntervalId);
      if (this.pollingTimeoutId) clearTimeout(this.pollingTimeoutId);
      this.pollingIntervalId = null;
      this.pollingTimeoutId = null;
      this.isLoading = false;
      if (isTimeout && !this.contratDetails) {
        this.$toasted.show(
          "Le paiement n'a pas pu être confirmé. Vous pouvez vérifier manuellement avec le bouton « Réactualiser ».",
          this.ToastTabError
        );
      }
    },
    startManualEntry() {
      this.isManualEntry = true;
      this.ocrSubmitted = true;
      this.isEditingOcr = true;
      // Reset OCR data for fresh manual entry
      this.ocrData = {
        nom: '',
        prenom: '',
        numeroPasseport: '',
        dateNaissance: '',
        dateExpiration: '',
        email: '',
        nationalite: '',
        paysResidence: '',
        genre: ''
      };
    },
    resetOcr() {
      this.ocrSubmitted = false;
      this.ocrError = null;
      this.passportFile = null;
      this.isManualEntry = false;
      this.isEditingOcr = false;
    },
    cancelEditOcr() {
      if (this.isManualEntry && !this.ocrData.nom) {
        this.resetOcr();
      } else {
        this.isEditingOcr = false;
      }
    },
    confirmEditOcr() {
      if (this.isOcrDataValid) {
        this.isEditingOcr = false;
      }
    },
    toggleEditOcr() {
      this.isEditingOcr = true;
    },
    restartSubscription() {
      this.stopPolling();
      this.step = 1;
      this.phoneNumber = '';
      this.destinationCountry = null;
      this.departureDate = null;
      this.passportFile = null;
      this.ocrSubmitted = false;
      this.ocrError = null;
      this.isEditingOcr = false;
      this.isManualEntry = false;
      this.ocrData = {
        nom: '',
        prenom: '',
        numeroPasseport: '',
        dateNaissance: '',
        dateExpiration: '',
        email: '',
        nationalite: '',
        paysResidence: '',
        genre: ''
      };
      this.selectedDuration = null;
      this.devis = null;
      this.contratDetails = null;
    }
  },
  async mounted() {
    // Récupération du code agent depuis les props (passées par le routeur)
    if (this.agentCodeFromUrl) {
      this.agentCode = this.agentCodeFromUrl;
    } else {
      // Fallback : détection manuelle si nécessaire
      const pathParts = window.location.pathname.split('/');
      const tukkiIndex = pathParts.indexOf('tukki');
      if (tukkiIndex !== -1 && pathParts[tukkiIndex + 1]) {
        this.agentCode = pathParts[tukkiIndex + 1];
      }
    }
    this.agentCode = this.normalizeAgentCode(this.agentCode);

    // Validation du code agent si présent
    if (this.agentCode) {
      this.isLoading = true;
      this.loadingMessage = 'Validation du code agent...';
      try {
        const result = await TukkiService.validateAgent(this.agentCode);
        if (result.valid) {
          this.isAgentValid = true;
          this.agentInfo = result.agent;
          this.$toasted.show(
            `Agent validé : ${result.agent.prenom} ${result.agent.nom}`,
            this.ToastTabSucess
          );
        } else {
          this.isAgentValid = false;
          this.agentCode = null;
          this.$toasted.show(result.message || 'Code agent invalide', this.ToastTabError);
        }
      } catch (error) {
        this.isAgentValid = false;
        this.agentCode = null;
        console.error('Erreur validation agent:', error);
      } finally {
        this.isLoading = false;
      }
    }

    try {
      const response = await countryController;
      this.listCountry = response.data.data || response.data;
      console.log('::::::: liste des pays', this.listCountry);
    } catch (error) {
      console.error('Erreur pays:', error);
    }
  }
};
</script>

<style scoped>
/* Form & Button Styles */
.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #495057;
  font-size: 15px;
}

.form-control-modern {
  display: block;
  width: 100%;
  padding: 12px 16px;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #212529;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 8px;
  transition:
    border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;
}

.form-control-modern:focus {
  color: #212529;
  background-color: #fff;
  border-color: #3d84f7;
  outline: 0;
  box-shadow: 0 0 0 4px rgba(61, 132, 247, 0.15);
}

.navigation-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
}

.btn-primary-modern,
.btn-secondary-modern {
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-primary-modern {
  background-color: #ffa409;
  color: #fff;
}

.btn-primary-modern:hover {
  background-color: #e89508;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.btn-secondary-modern {
  background-color: #f0f2f5;
  color: #495057;
}

.btn-secondary-modern:hover {
  background-color: #e2e6ea;
  transform: translateY(-2px);
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
}

.modal-content {
  background-color: white;
  padding: 30px;
  border-radius: 8px;
  width: 90%;
  max-width: 400px;
  text-align: left;
}

.modal-content h3 {
  text-align: center;
  margin-bottom: 20px;
}

.modal-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}

.modal-actions .btn-secondary {
  background-color: #6c757d;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  color: white;
}

.loading-spinner {
  text-align: center;
}

.loading-spinner .fa-spinner {
  font-size: 38px;
  margin-bottom: 15px;
}

.loading-spinner p {
  font-size: 16px;
}

.devis-summary {
  background-color: #f9f9f9;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
  text-align: left;
}

.insurer-info {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.insurer-logo {
  width: 50px;
  height: 50px;
  margin-right: 15px;
}

.devis-details p {
  margin: 5px 0;
}

.price {
  margin-top: 15px;
  font-size: 1.2em;
  font-weight: bold;
}

.price span {
  color: #007bff;
}

.form-group {
  margin-bottom: 15px;
  text-align: left;
}

label {
  display: block;
  margin-bottom: 5px;
}

input[type='date'] {
  width: 100%;
  padding: 10px;
  font-size: 16px;
  border-radius: 5px;
  border: 1px solid #ccc;
  box-sizing: border-box;
  margin-bottom: 10px;
}

input[type='tel'],
input[type='text'],
input[type='number'] {
  width: 100%;
  padding: 10px;
  font-size: 16px;
  border-radius: 5px;
  border: 1px solid #ccc;
  box-sizing: border-box;
  margin-bottom: 10px;
}

/*button {
  padding: 12px 25px;
  font-size: 18px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  margin-top: 10px;
}*/

button:disabled {
  /*//background-color: #ccc;*/
  cursor: not-allowed;
}

button .fa-spinner {
  margin-right: 8px;
}

.step-indicator {
  font-size: 14px;
  color: #666;
  margin-bottom: 15px;
}

.upload-section {
  margin: 20px 0;
}

.upload-box {
  border: 2px dashed #ccc;
  border-radius: 10px;
  padding: 30px;
  margin-bottom: 20px;
  cursor: pointer;
}

.upload-box:hover {
  border-color: #ffa409;
}

.upload-label {
  font-size: 18px;
  color: #333;
}

.file-input {
  display: none;
}

.error-message {
  color: #e53e3e;
  background-color: #fed7d7;
  border: 1px solid #fbb6b6;
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1.5rem;
  text-align: center;
}

.duration-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin: 20px 0;
}

@media (max-width: 480px) {
  .duration-options {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
  .duration-btn {
    padding: 12px;
  }
  .duration-label {
    font-size: 14px;
  }
  .duration-price {
    font-size: 13px;
  }
}

@media (max-width: 360px) {
  .duration-options {
    grid-template-columns: 1fr;
  }
}

.duration-btn {
  background-color: #f8fafc;
  color: #0d4e78;
  border: 2px solid #e2e8f0;
  padding: 15px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.duration-btn:hover {
  border-color: #ffa409;
  background-color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.duration-btn.selected {
  background-color: #fff8ed;
  color: #ffa409;
  border-color: #ffa409;
  box-shadow: 0 0 0 3px rgba(255, 164, 9, 0.1);
}

.duration-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.duration-label {
  font-size: 16px;
  font-weight: 600;
}

.duration-price {
  font-size: 14px;
  opacity: 0.8;
  font-weight: 500;
}

.duration-btn.selected .duration-price {
  color: #e89508;
}

/* ======== Nouveau design page paiement (Step 4) ======== */
.checkout-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 22px;
  box-shadow: 0 4px 14px rgba(13, 78, 120, 0.06);
  text-align: left;
  margin-top: 16px;
}

.checkout-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid #f1f5f9;
}

.checkout-icon {
  font-size: 18px;
  color: #ffa409;
  background: rgba(255, 164, 9, 0.1);
  border-radius: 8px;
  padding: 8px 10px;
}

.checkout-title {
  font-size: 16px;
  font-weight: 700;
  color: #0d4e78;
  margin: 0;
}

.checkout-rows {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.checkout-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  font-size: 14px;
}

.checkout-label {
  color: #6b7280;
  font-weight: 500;
  flex-shrink: 0;
}

.checkout-value {
  color: #0d4e78;
  font-weight: 600;
  text-align: right;
  word-break: break-word;
}

/* Breakdown des frais */
.checkout-breakdown {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px dashed #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.checkout-breakdown-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 13px;
}

.checkout-breakdown-label {
  color: #6b7280;
  font-weight: 500;
}

.checkout-breakdown-value {
  color: #0d4e78;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.checkout-total {
  margin-top: 18px;
  padding: 16px 18px;
  background: linear-gradient(135deg, #fff8ed 0%, #fff 100%);
  border: 1px solid #ffa409;
  border-radius: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.checkout-total-label {
  font-size: 14px;
  font-weight: 700;
  color: #0d4e78;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.checkout-total-amount {
  font-size: 26px;
  font-weight: 800;
  color: #ffa409;
  white-space: nowrap;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}

.checkout-total-currency {
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  margin-left: 2px;
}

.payment-section-title {
  font-size: 14px;
  font-weight: 600;
  color: #0d4e78;
  margin: 0 0 12px;
  text-align: center;
}

.payment-options .payment-buttons {
  display: flex;
  gap: 14px;
  justify-content: center;
  margin-top: 6px;
  flex-wrap: wrap;
}

.payment-options .payment-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 18px 22px;
  border-radius: 14px;
  border: 1.5px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: #fff;
  min-width: 200px;
  font-size: 13px;
  font-weight: 600;
  color: #0d4e78;
}

.payment-options .payment-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
}

.payment-options .payment-btn img {
  height: 44px;
  object-fit: contain;
}

.payment-btn-meta {
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.2;
}

.payment-btn-label {
  font-size: 14px;
  font-weight: 700;
  color: #0d4e78;
}

.payment-btn-sublabel {
  margin-top: 2px;
  font-size: 11px;
  font-weight: 500;
  color: #22c55e;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.payment-options .payment-btn.wave {
  border-color: #41c3ff;
}

.payment-options .payment-btn.wave:hover {
  background: #f0faff;
  border-color: #1ea7e6;
}

.payment-options .payment-btn.om {
  border-color: #ff7800;
}

.payment-options .payment-btn.om:hover {
  background: #fff5eb;
  border-color: #e16800;
}

.payment-security-note {
  margin-top: 14px;
  text-align: center;
  font-size: 12px;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.payment-security-note i {
  color: #22c55e;
}

@media (max-width: 600px) {
  .checkout-card {
    padding: 16px;
    border-radius: 12px;
  }
  .checkout-title {
    font-size: 15px;
  }
  .checkout-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }
  .checkout-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
  .checkout-value {
    text-align: left;
    font-size: 14px;
  }
  .checkout-breakdown {
    margin-top: 14px;
    padding-top: 12px;
  }
  .checkout-breakdown-row {
    font-size: 13px;
  }
  .checkout-total {
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 16px 14px;
    text-align: center;
  }
  .checkout-total-label {
    font-size: 12px;
  }
  .checkout-total-amount {
    font-size: 30px;
  }
  .payment-section-title {
    font-size: 13px;
    margin-bottom: 10px;
  }
  .payment-options .payment-buttons {
    flex-direction: column;
    gap: 10px;
    margin-top: 0;
  }
  .payment-options .payment-btn {
    width: 100%;
    flex-direction: row;
    justify-content: flex-start;
    padding: 14px 18px;
    gap: 14px;
    min-width: 0;
  }
  .payment-options .payment-btn img {
    height: 36px;
  }
  .payment-btn-meta {
    align-items: flex-start;
  }
  .payment-security-note {
    margin-top: 10px;
    font-size: 11px;
  }
}

.confirmation-summary {
  background-color: #f0f8ff;
  border: 1px solid #add8e6;
  border-radius: 8px;
  padding: 15px;
  margin: 20px 0;
  text-align: left;
  font-size: 13px;
}

.confirmation-summary ul {
  list-style: none;
  padding: 0;
}

.confirmation-summary li {
  margin-bottom: 8px;
}

.sharing-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.btn-refresh {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f0f2f5;
  border: 1px solid #e2e8f0;
  padding: 8px 16px;
  border-radius: 8px;
  color: #0d4e78;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-refresh:hover:not(:disabled) {
  background: #e2e8f0;
  border-color: #cbd5e1;
  transform: translateY(-1px);
}

.btn-refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error-text {
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
  display: block;
}

.sharing-options {
  text-align: center;
  margin-top: 2rem;
}

.share-buttons-container {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 1rem;
}

.share-btn {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.8rem 1.2rem;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1.2rem;
  font-weight: 500;
  color: #fff;
}

.share-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.share-btn i {
  font-size: 1.2rem;
  width: auto;
  text-align: center;
}

.share-btn.pdf {
  background-color: #d9534f;
}
.share-btn.whatsapp {
  background-color: #25d366;
}
.share-btn.sms {
  background-color: #f0ad4e;
}
</style>

<style scoped>
/* OCR Details Styles */
.ocr-details-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e9ecef;
  margin-bottom: 20px;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.detail-item {
  display: flex;
  flex-direction: column;
}

.detail-label {
  font-size: 11px;
  color: #6c757d;
  text-transform: uppercase;
  font-weight: 700;
  margin-bottom: 4px;
}

.detail-value {
  font-size: 14px;
  color: #0d4e78;
  font-weight: 600;
}

.btn-outline-primary {
  transition: all 0.3s;
  border: 1px solid #ffa409;
  background-color: transparent;
  color: #ffa409;
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-outline-primary:hover {
  background: #ffa409 !important;
  color: #fff !important;
}

.btn-outline-primary:disabled {
  border-color: #d3d3d3;
  color: #d3d3d3;
  cursor: not-allowed;
}

@media (max-width: 600px) {
  .card-footer {
    padding: 1rem;
  }

  .actions.step2-actions {
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }

  .actions.step2-actions .btn-primary-modern,
  .actions.step2-actions .btn-outline-primary,
  .actions.step2-actions .wizard-btn.back-btn,
  .card-footer > .btn-primary-modern {
    width: 100% !important;
    margin: 0 !important;
    justify-content: center;
    padding: 0.7rem 1rem;
    font-size: 1.2rem;
    height: auto;
    min-height: 44px;
  }

  .card-footer {
    flex-direction: column;
    gap: 10px;
  }

  /* Pour l'étape 2 spécifiquement, on veut le bouton Suivant en premier (en haut) */
  .actions.step2-actions {
    display: flex;
    flex-direction: column-reverse;
  }

  .actions.step2-actions .btn-primary-modern {
    order: -1;
    margin-bottom: 4px !important;
  }
}

.phone-input-wrapper {
  display: flex;
  align-items: stretch;
  gap: 0;
}

.phone-field-input {
  border-radius: 0 10px 10px 0 !important;
  border-left: none !important;
}
</style>
